import { cloneDeep } from 'lodash/fp';
import {
  findParentElement,
  getStyleNumber,
  parseHtmlText,
  isEmptyString,
} from '.';

/**
 * Grid 세팅하는 함수
 * @param {string | HTMLElement} id Grid를 생성할 HTMLElement의 ID String
 *                                 (혹은 이전에 작성된 setGrid() 경우, id값이 있는 HTMLElement)
 * @param {GridOptions} options Grid 생성시의 옵션들
 * @return {Promise<void>}
 * @author Robin
 */
export const setGrid = (id, options = {}) => {
  return new Promise((resolve, reject) => {
    try {
      const {
        colModel: optionColModel,
        pager,
        rowNum,
        rowList,
        rownumbers,
        rownumberLabel,
        loadtext,
        isHideLoadText,
        emptyrecords,
        remapColumns,
        multiselect,
        resizeStop,
        loadBeforeSend,
        loadComplete,
        onSelectRow,
      } = options;

      // setGrid(element, options) 함수 일 때 추가된 첫 번째인자가 HTMLElement/JQuery Object 일 경우,
      // 해당 element에서 id 값을 빼서 사용하며,
      // 입력된 id에 맨 앞에 #이 포함되어 selector 형태로 온 경우, 해당 #을 제거하고 사용한다.
      const gridSelector = typeof id === 'object' ? id.id || id[0]?.id : id;
      const gridId =
        gridSelector[0] === '#' ? gridSelector.slice(1) : gridSelector;
      const gridObject = $(`#${gridId}`);
      const gridWrap = gridObject[0];
      if (!gridWrap)
        return console.error(
          'setGrid() - gridId에 해당하는 HTMLElement 가 없습니다.',
        );

      // 로딩 상태 스타일 설정
      const gridParentWrap = findParentElement(gridWrap, '.gridArea');
      gridParentWrap.classList.add('grid-loading');

      // pager 설정
      const pagerWrap = pager ? gridParentWrap.querySelector(pager) : null;
      if (pagerWrap) pagerWrap.classList.add('pager');

      // Grid 생성 시의 옵션 저장
      saveGridOptions(gridId, cloneDeep(options));

      // colModel에서 정렬은 기본적으로는 false 설정
      const colModel = optionColModel.map(column => ({
        sortable: false,
        ...column,
      }));

      // 조회 텍스트 옵션이 true 일 경우, "조회: 0건" 텍스트 노출을 위해 우선 추가
      const isLoadText = loadtext || (pager && !isHideLoadText);
      if (isLoadText) window.gridPager.setLoadText({ id: gridId });

      // 함수 중복 실행 방지 Flag
      const runFunctions = [];

      const gridOptions = {
        ...options,

        // 수정된 colModel 추가
        colModel,

        // pager 값은 loadComplete() 에서 pagination을 추가하는 것으로 대체하여 제거
        pager: null,

        // 헤더 Resize 후 실행되는 함수
        resizeStop: (width, index) => {
          // 헤더 Resize 시, 너비가 틀어지는 현상 보정
          const headerFirstRow = gridParentWrap.querySelector(
            '.jqg-first-row-header',
          );
          if (headerFirstRow) {
            const headerFirstRowTargetColumn =
              headerFirstRow.querySelectorAll('td, th')[index];
            headerFirstRowTargetColumn.style.width = `${width}px`;
          }

          if (typeof resizeStop === 'function') resizeStop(width, index);
        },

        // Grid 데이터 Load 전 호출되는 함수
        loadBeforeSend(xhr) {
          // FrozenColumns 관련 엘리먼트 있으면 제거
          const frozenColumnSelectors = ['.frozen-div', '.frozen-bdiv'];
          const frozenColumnElements = gridParentWrap.querySelectorAll(
            frozenColumnSelectors.join(','),
          );
          frozenColumnElements.forEach(frozenColumnElement => {
            frozenColumnElement.remove();
          });

          // // API 요청에 Auth Headers 추가
          // if (typeof window.api?.getAuthHeader === 'function') {
          //   const headers = window.api.getAuthHeader();
          //   Object.keys(headers).forEach(key => {
          //     const value = headers[key];
          //     xhr.setRequestHeader(key, value);
          //   });
          // }

          // Multi Header 설정 안된 경우 (여러줄 헤더) 설정
          if (!runFunctions.includes('setMultiHeaders')) {
            setGridHeader(gridId, options);
            runFunctions.push('setMultiHeaders');
          }

          if (typeof loadBeforeSend === 'function') loadBeforeSend(xhr);
        },

        // Grid 데이터 Load 완료 후 호출되는 함수
        async loadComplete(...args) {
          // 함수 중복 실행 방지용 로직 추가
          const isAlreadyRun = runFunctions.includes('loadComplete');
          if (isAlreadyRun) return;
          else runFunctions.push('loadComplete');

          // 페이저가 없고 조회 텍스트만 있을 경우 설정
          if (!pager && isLoadText)
            window.gridPager.setLoadText({ id: gridId });

          // Pager 값이 추가되어 있는 경우, Pagination 추가
          if (pager)
            window.gridPager.initPage({
              id: gridId,
              pager,
              rowNum,
              rowList,
              isLoadText,
            });

          // 조회된 Data 없을 때 메시지를 추가하고 싶은 경우 사용
          if (emptyrecords)
            window.gridPager.setEmptyMessage({
              id: gridId,
              message: emptyrecords,
            });

          // rownumber 칼럼에 Label 추가
          if (rownumbers && rownumberLabel)
            gridObject.jqGrid('setLabel', 'rn', rownumberLabel);

          // remapColumns가 있는 경우, 칼럼 순서 재설정
          if (Array.isArray(remapColumns)) {
            const isColumnResetted =
              gridWrap.getAttribute('is-column-resetted') === 'true';
            if (!isColumnResetted)
              gridObject.remapColumns(remapColumns, remapColumns);
            gridWrap.setAttribute('is-column-resetted', 'true');
          }

          // colModel 중 sortable: true 인 칼럼에 .sortable-icon 클래스 추가
          gridObject.jqGrid('getGridParam', 'colModel').forEach((col, i) => {
            if (col.sortable) {
              gridObject
                .closest('div.ui-jqgrid-view')
                .find(`th.ui-th-column:eq(${i})`)
                .addClass('sortable-icon');
            }
          });

          // Multi Header 설정 안된 경우 (여러줄 헤더) 설정
          if (!runFunctions.includes('setMultiHeaders')) {
            setGridHeader(gridId, options);
            runFunctions.push('setMultiHeaders');
          }

          // 그리드 내에 Input이 있는 경우, onchange에 함수 바인딩
          const inputs = document.querySelectorAll(`#${gridId} input`);
          inputs.forEach(input => {
            input.addEventListener(
              'change',
              window.gridPager.setAttributeValue,
            );
          });

          // 틀고정(Frozon Column 설정)
          const frozenColumns = colModel.filter(col => col.frozen);
          const frozenColumnCount = frozenColumns.length;
          const isFrozenColumn = frozenColumnCount > 0;
          if (isFrozenColumn) {
            // 1. setFrozonColumns 세팅
            gridObject.jqGrid('setFrozenColumns');

            // 2. FrozenColums에 해당하는 테이블 Body 내용 복사
            const gridTableWrap =
              gridParentWrap.querySelector('.ui-jqgrid-btable');
            const frozenBodyWrap = gridParentWrap.querySelector('.frozen-bdiv');
            const frozenTableWrap = gridTableWrap.cloneNode(true);
            frozenBodyWrap.appendChild(frozenTableWrap);

            // 3. frozenColums에 해당하는 칼럼 외 나머지 칼럼 td 제거 및 td의 높이 설정
            const frozenGridTrs = frozenTableWrap.querySelectorAll('tr');
            const frozenColumnIndex = frozenColumns.findLastIndex(
              frozenColumn => frozenColumn.frozen,
            );
            frozenGridTrs.forEach((tr, trIndex) => {
              // td의 높이 구하기
              let tdHeight = '0px';
              const originalTableTds = gridTableWrap.querySelectorAll(
                `.ui-jqgrid-bdiv:not(.frozen-bdiv.ui-jqgrid-bdiv) tr:nth-child(${
                  trIndex + 1
                }) td`,
              );
              originalTableTds.forEach(td => {
                const tdHeightNumber = getStyleNumber(tdHeight);
                const targetTdHeight = window.getComputedStyle(td).height;
                const targetTdHeightNumber = getStyleNumber(targetTdHeight);
                if (tdHeightNumber < targetTdHeightNumber)
                  tdHeight = targetTdHeight;
              });

              // frozenColums에 해당하는 칼럼에 높이 설정 및 해당하지 않는 칼럼은 제거
              const tds = tr.querySelectorAll('td');
              tds.forEach((td, tdIndex) => {
                if (tdIndex - 1 > frozenColumnIndex) td.remove();
                else if (trIndex > 0) td.style.height = tdHeight;
              });
            });

            // 4. FrozonColumns의 안에 Grid 기본 체크박스(gridFrozenColumn_cb)가 있을 경우 스타일이 깨져 전부 안보이게 처리
            const frozenColumnCbItems = frozenTableWrap.querySelectorAll(
              '[aria-describedby=gridFrozenColumn_cb] input[type=checkbox], [aria-describedby=gridFrozenColumn_cb] label',
            );
            frozenColumnCbItems.forEach(
              frozenColumnCbItem =>
                (frozenColumnCbItem.style.visibility = 'hidden'),
            );

            // 5. FrozenColumns의 header/body 스타일 보정
            const gridScrollSize = 16;
            const frozenGridBodyTop = frozenBodyWrap.style.top;
            frozenTableWrap.style.width = '1px';
            frozenBodyWrap.style.pointerEvents = 'none';
            frozenBodyWrap.style.height = `calc(100% - ${frozenGridBodyTop} - ${gridScrollSize}px)`;

            // 6. Grid 안의 항목들의 width 전체 조정
            const gridAreaWidth = window.getComputedStyle(gridParentWrap).width;
            const widthTargetSelectors = [
              '.ui-jqgrid',
              '.ui-jqgrid-view',
              '.ui-jqgrid-hdiv:not(.frozen-div.ui-jqgrid-hdiv)',
              '.ui-jqgrid-htable:not(.frozen-div > .ui-jqgrid-htable)',
              '.ui-jqgrid-bdiv:not(.frozen-bdiv.ui-jqgrid-bdiv)',
            ];
            const widthTargets = gridParentWrap.querySelectorAll(
              widthTargetSelectors.join(','),
            );
            widthTargets.forEach(widthTarget => {
              widthTarget.style.width = gridAreaWidth;
            });
          }

          // 원래 Grid 생성 시의 loadComplete() 함수 실행
          if (typeof loadComplete === 'function') await loadComplete(...args);

          // 로딩 상태 스타일 해제
          gridParentWrap.classList.remove('grid-loading');

          // Grid 로딩 완료
          resolve();
        },

        // multiselect != true 일 때, 체크해제 이벤트 추가
        onSelectRow(...args) {
          const isChecked = args[1];
          if (!multiselect && !isChecked) gridObject.jqGrid('resetSelection');

          // 틀고정이 있는 경우를 확인하여, 틀고정 항목에도 Select Highlight 추가
          const frozenBodyWrap = gridParentWrap.querySelector('.frozen-bdiv');
          if (frozenBodyWrap) {
            const rowIndex = parseInt(args[0]);
            const frozenColumnRow = frozenBodyWrap.querySelector(
              `tr:nth-child(${rowIndex + 1})`,
            );
            if (isChecked) frozenColumnRow.classList.add('ui-state-highlight');
            else frozenColumnRow.classList.remove('ui-state-highlight');
          }

          // 원래 Grid 생성 시의 onSelectRow() 함수 실행
          if (typeof onSelectRow === 'function') onSelectRow(...args);
        },
      };

      gridObject.jqGrid(gridOptions);
    } catch (error) {
      console.error('setGrid() - error', error);
      console.error(
        'setGrid(id, options) - id는 Grid가 생성될 Element의 유니크한 ID이어야 합니다.',
      );

      reject(error);
    }
  });
};

/**
 * Grid Header의 Multi Header와 Rowspan 등을 세팅하는 함수
 * @param {string} gridId Grid를 생성할 HTMLElement의 ID String
 * @param {GridOptions} options Grid 생성시의 옵션들
 * @return {void}
 * @author Robin
 */
export const setGridHeader = (gridId, options = {}) => {
  const { colModel = [], multiHeaders = [] } = options;
  const gridObject = $(`#${gridId}`);
  const gridWrap = gridObject[0];
  const gridParentWrap = findParentElement(gridWrap, '.gridArea');

  // Multi Header 설정 안된 경우 (여러줄 헤더) 설정
  multiHeaders.forEach(headers => {
    const isHeaders = Array.isArray(headers) && headers.length > 0;
    if (isHeaders)
      gridObject.jqGrid('setGroupHeaders', { groupHeaders: headers });
  });

  // jqGrid에서 자동으로 맨앞에 추가하는 칼럼들을 index 계산에 포함하기 위해 첫 칼럼 시작 index 계산
  const jqGridExtraColumnOptions = ['rownumbers', 'multiselect'];
  const defaultColumnCount = jqGridExtraColumnOptions.reduce(
    (count, optionName) => {
      if (options[optionName]) count++;
      return count;
    },
    0,
  );

  // rowspan을 위해 각 셀에 Selector 설정
  const headerWrap = gridParentWrap.querySelector('thead');
  // 첫 줄에 노출되지 않는 <tr>이 존재하여, 첫 번째 항목 제거
  const headerRows = Object.values(headerWrap.querySelectorAll('tr')).slice(1);
  const rowCount = headerRows.length;
  headerRows.forEach(tr => {
    const colItems = Object.values(tr.childNodes).slice(defaultColumnCount);

    let colspanLength = 0;
    colItems.forEach((colItem, tdIndex) => {
      // 칼럼들에 해당 colName이 들어간 class이름 추가
      const targetTdIndex = colspanLength + tdIndex;
      const name = colModel[targetTdIndex]?.name;
      colItem.classList.add(`col-cell-${name}`);

      // colspanLength 값 조정
      const itemColspanAdjust =
        parseInt(colItem.getAttribute('colspan') || 1) - 1;
      colspanLength = colspanLength + itemColspanAdjust;
    });
  });

  colModel.map(colModelInfo => {
    const { name, rowspan } = colModelInfo;
    if (!rowspan) return;

    const targetColSelector = `#${gridId}_${name}`;
    const targetRowspanElement =
      gridParentWrap.querySelector(targetColSelector);
    if (!targetRowspanElement) return;

    const rowspanLineIndex = rowCount - rowspan;

    // rowspan 적용 후, 이로 인해 불필요한 <th>, <td> 제거
    headerRows.forEach((headerRow, headerRowIndex) => {
      // 현재 Row가 rowspan이 일어난 Row 인지 여부
      const isRowspanRow = rowspanLineIndex === headerRowIndex;

      // 마지막 row 면 리턴
      const isLastRow = headerRows.length - 1 === headerRowIndex;
      if (isLastRow) return;

      // 병합된 row 아래 줄이 아니라면 리턴
      const isMergedRow = rowspanLineIndex <= headerRowIndex;
      if (!isMergedRow) return;

      // rowspan을 진행할 row에서, 병합하고 없앨 element를 구함
      const removeElementClass = `.col-cell-${name}`;
      const removeElement = headerRow.querySelector(removeElementClass);

      // rowspan을 진행할 Row 라면 병합하고 없앨 removeElement를 targetRowspanElement로 대체하여 지움
      if (isRowspanRow) {
        removeElement.replaceWith(targetRowspanElement);
        targetRowspanElement.setAttribute('rowspan', rowspan);
      }

      // rowspan을 진행할 열이 아닌 그 아래 Row 들이라면, removeElement를 지움
      else {
        removeElement.remove();
      }
    });
  });
};

/**
 * Grid를 변경된 options으로 Reload하는 함수
 * @param {string} id Grid를 생성할 HTMLElement의 ID String
 * @param {GridOptions} options Grid Reload 시에 기존 그리드에서 변경할 옵션들
 * @return {Promise<void>}
 * @author Robin
 */
export const reloadGrid = (id, options = {}) => {
  const { parentSelector = '.gridArea' } = options;

  // Store에 저장되어 있는 Grid의 예전 Options
  let oldGridOptions = loadGridOptions(id);

  // 이전 Grid Params가 없으면 jqGrid의 getGridParam() 함수로 세팅
  if (!oldGridOptions) {
    // Grid의 이전 Params 불러와서 세팅
    const gridSelector = `#${id}`;
    const gridObject = $(gridSelector).jqGrid();
    oldGridOptions = gridObject.getGridParam();
  }

  const gridOptions = {
    ...oldGridOptions,
    ...options,
  };

  // Grid 초기화
  clearGrid(id, { parentSelector });

  // Grid Params로 재생성
  return setGrid(id, gridOptions);
};

/**
 * Grid를 jqGrid 만들기 이전의 상태(<table id="${id}"></table>)로 돌리는 함수
 * @param {string} id Grid를 생성할 HTMLElement의 ID String
 * @param {GridOptions} options Grid Reload 시에 기존 그리드에서 변경할 옵션들
 * @return {void}
 * @author Robin
 */
export const clearGrid = (id, options = {}) => {
  const { parentSelector = '.gridArea' } = options;

  const gridSelector = `#${id}`;
  const gridWrap = document.querySelector(gridSelector);
  const gridParentWrap = findParentElement(gridWrap, parentSelector);
  gridParentWrap.innerHTML = `<table id="${id}"></table>`;

  // Store에 저장되어 있는 Grid의 예전 Options
  const oldGridOptions = loadGridOptions(id);
  const pager = oldGridOptions?.pager;

  // pager가 있는경우, pager 다시 추가
  if (pager) {
    const pagerId = pager.replace('#', '');
    const pagerWrap = parseHtmlText(`
      <div id="${pagerId}"></div>
    `);
    gridParentWrap.appendChild(pagerWrap);
  }
};

(function () {
  window.gridPager = {
    initPage(options = {}) {
      const gridPager = new GridPager(options);
      return gridPager;
    },

    /* 총 조회 건수 텍스트 표시 */
    setLoadText(options = {}) {
      let { id, totalSize = 0 } = options;

      const gridSelector = `#${id}`;
      const gridWrap = $(gridSelector);
      const gridAreaWrap = gridWrap.parents('.gridArea')[0];
      let loadTextContainer = gridAreaWrap;

      // rowListSelect(행 개수 선택 Select)가 존재하는 경우, loadText를 rowListSelectWrap(.options)에 추가
      const rowListSelectWrap = gridAreaWrap.querySelector(
        '.pagingWrap .options',
      );
      if (rowListSelectWrap) loadTextContainer = rowListSelectWrap;

      // Total Size가 없을 경우, Grid Params 에서 불러옴
      if (!totalSize) totalSize = gridWrap.jqGrid('getGridParam', 'records');

      // 기존에 존재하는 loadText가 있을 경우, 제거 하고 다시 생성
      const loadTextClassName = 'load-text';
      const existingLoadTextWrap = gridAreaWrap.querySelector(
        `.${loadTextClassName}`,
      );
      if (existingLoadTextWrap) existingLoadTextWrap.remove();

      // 새로운 loadTextWrap 생성
      const loadTextWrap = document.createElement('div');
      loadTextWrap.className = loadTextClassName;
      loadTextContainer.append(loadTextWrap);

      const totalSizeNumber = window.parseInt(totalSize || 0);
      const totalSizeText = totalSizeNumber.toLocaleString();
      const loadedTotalText = `총 ${totalSizeText}건`;
      loadTextWrap.innerHTML = `(${loadedTotalText})`;
    },

    // 데이터가 없을 경우 메시지 추가
    setEmptyMessage(options = {}) {
      const { id: gridId, message = '조회된 데이터가 없습니다.' } = options;
      const gridWrap = $(`#${gridId}`);
      const gridContentWrap = $(`#${gridId}`).closest('.ui-jqgrid-bdiv');
      const gridDataListWrap = $(`#${gridId} tbody tr`);

      // 기존 emptyMessageWrap이 있을 경우, 지우고 재생성
      const emptyMessageWrap = $(`#${gridId}-empty`);
      if (emptyMessageWrap) emptyMessageWrap.remove();

      const recordCount = gridWrap.jqGrid('getGridParam', 'reccount') || 0;
      const isEmpty = recordCount <= 0;

      if (isEmpty) {
        const newEmptyMessageWrap = document.createElement('div');
        newEmptyMessageWrap.id = `${gridId}-empty`;
        newEmptyMessageWrap.className = `noData`;
        newEmptyMessageWrap.innerHTML = message;
        gridContentWrap.append(newEmptyMessageWrap);
        gridDataListWrap.hide();
      } else {
        gridDataListWrap.show();
      }
    },

    // Grid 안에 <input> 태그가 있을 경우, value 변경을 위해 onchange에 추가하는 함수
    setAttributeValue(event) {
      event.target.setAttribute('value', event.target.value);
    },
  };
})();

class GridPager {
  gridId = '';
  pager = '';
  pageCount = 1;
  pageViewCount = 10;
  currentPageList = 0;
  totalPageListCount = 0;
  isLoadText = false;

  constructor(options) {
    let {
      id = '',
      pager,
      pageViewCount = 10,
      totalSize,
      currentPage,
      rowList,
      isLoadText: isShowLoadText,
    } = options;
    this.isLoadText = isShowLoadText;
    this.gridId = id;
    this.pager = isEmptyString(pager) ? pager : this.pager;
    const pagerSelector = this.pager || '#pager';

    const gridWrap = $('#' + this.gridId);
    const gridAreaWrap = gridWrap.parents('.gridArea');
    const pagerWrap = gridAreaWrap.find(pagerSelector)[0];

    // Pager가 없는 경우 return
    if (!pagerWrap) return;

    // Pager가 있으면, Pager 초기화 및 PagingWrap 추가
    const pagerPagingWrap = parseHtmlText(`
      <div class="paging"></div>
    `);
    pagerWrap.innerHTML = '';
    pagerWrap.classList.add('pagingWrap');
    pagerWrap.appendChild(pagerPagingWrap);

    // 전체 항목 개수가 없으면 gridWrap 에서 받는다
    if (!totalSize) totalSize = gridWrap.jqGrid('getGridParam', 'records');

    // 변수로 그리드아이디, 총 데이터 수, 현재 페이지를 받는다
    if (!currentPage) currentPage = gridWrap.getGridParam('page');

    this.pageViewCount = pageViewCount; // 한 화면에 보여줄 페이지 개수 (ex: 10 => 1 2 3 4 5 6 7 8 9 10)
    const rowNum = gridWrap.getGridParam('rowNum') || 10; // 한 페이지에 보여줄 Row의 개수
    this.pageCount = Math.ceil(totalSize / rowNum); // 그리드 데이터 전체의 페이지 수
    this.totalPageListCount = Math.ceil(this.pageCount / this.pageViewCount); // 전체 페이지 수를 한화면에 보여줄 페이지로 나눈다.
    this.currentPageList = Math.ceil(currentPage / this.pageViewCount); // 페이지 리스트가 몇번째 리스트인지

    if (this.currentPageList < 1) this.currentPageList = 1; // 페이지 리스트가 1보다 작으면 1로 초기화
    if (this.currentPageList > this.totalPageListCount)
      this.currentPageList = this.totalPageListCount; // 페이지 리스트가 총 페이지 리스트보다 커지면 총 페이지 리스트로 설정

    let startPageList = (this.currentPageList - 1) * this.pageViewCount + 1; // 시작 페이지
    let endPageList = startPageList + this.pageViewCount - 1; // 끝 페이지

    // 시작 페이지와 끝페이지가 1보다 작으면 1로 설정
    // 끝 페이지가 마지막 페이지보다 클 경우 마지막 페이지값으로 설정
    if (startPageList < 1) startPageList = 1;
    if (endPageList > this.pageCount) endPageList = this.pageCount;
    if (endPageList < 1) endPageList = 1;

    // 이전 페이지 이동 버튼 설정
    const firstBtn = parseHtmlText(
      '<button type="button" class="icoBtn_first" aria-label="처음"></button>',
    );
    const prevBtn = parseHtmlText(
      '<button type="button" class="icoBtn_prev" aria-label="이전"></button>',
    );
    firstBtn.onclick = () => this.firstPage();
    prevBtn.onclick = () => this.prevPage();

    pagerPagingWrap.appendChild(firstBtn);
    pagerPagingWrap.appendChild(prevBtn);

    // 페이지 숫자를 찍으며 태그생성 (현재 페이지는 강조태그)
    for (let i = startPageList; i <= endPageList; i++) {
      const pageBtn = parseHtmlText(
        `<a href="javascript:void(0)" id="${i}" class="grid-page-btn" pageIndex="${i}"></a>`,
      );
      if (i == currentPage) {
        pageBtn.classList.add('on');
        pageBtn.innerHTML = `<em>${i}</em>`;
      } else pageBtn.innerHTML = i;

      pageBtn.onclick = () => this.goPage(i);
      pagerPagingWrap.appendChild(pageBtn);
    }

    // 다음 페이지 이동 버튼 설정
    const nextBtn = parseHtmlText(
      `<button type="button" class="icoBtn_next" aria-label="다음"></button>`,
    );
    const lastBtn = parseHtmlText(
      `<button type="button" class="icoBtn_last" aria-label="마지막"></button>`,
    );
    nextBtn.onclick = () => this.nextPage();
    lastBtn.onclick = () => this.lastPage();

    pagerPagingWrap.appendChild(nextBtn);
    pagerPagingWrap.appendChild(lastBtn);

    // rowList가 Array 존재하면 rowSelector 추가
    const isRowList = Array.isArray(rowList) && rowList.length > 0;
    if (isRowList) this.addRowSelect(pagerWrap, options);

    // isLoadText 값이 true 면, 조회 Text 값 추가
    if (this.isLoadText) window.gridPager.setLoadText({ id, totalSize });

    this.stylePageBtn();
  }

  addRowSelect(pagerWrap, options) {
    const { rowNum = 10, rowList } = options;
    const rowSelectorId = `${this.gridId}-rowSelector`;
    const rowSelector = parseHtmlText(`
      <select id="${rowSelectorId}" class="ipt notDel uiAct" name="${rowSelectorId}"></select>
    `);
    rowSelector.onchange = event => {
      const numberValue = window.parseInt(event.target.value);
      const newRowNum = isNaN(numberValue) ? rowNum : numberValue;
      reloadGrid(this.gridId, { rowNum: newRowNum });
    };

    rowList.forEach(rowCount => {
      const isCurrentRowNum = rowNum === rowCount;
      const rowOption = parseHtmlText(`
        <option value="${rowCount}" ${
        isCurrentRowNum ? 'selected' : ''
      }>${rowCount}개씩보기</option>
      `);
      rowSelector.appendChild(rowOption);
    });

    const rowSelectorWrap = parseHtmlText(`<div class="options"></div>`);
    rowSelectorWrap.appendChild(rowSelector);
    pagerWrap.appendChild(rowSelectorWrap);
  }

  // 그리드 첫페이지로 이동
  firstPage() {
    reloadGrid(this.gridId, { page: 1 });
    this.stylePageBtn();
  }

  // 그리드 이전페이지 이동
  prevPage() {
    let currentPage = $(`#${this.gridId}`).getGridParam('page');
    const isFirstPage = currentPage <= 1;
    if (isFirstPage) return;

    const pageListIndex = Math.ceil(currentPage / this.pageViewCount);
    this.currentPageList = pageListIndex - 1;
    currentPage = Math.max(this.currentPageList * this.pageViewCount, 1);

    reloadGrid(this.gridId, { page: currentPage });
    this.stylePageBtn();
  }

  // 그리드 다음페이지 이동
  nextPage() {
    let currentPage = $(`#${this.gridId}`).getGridParam('page');
    const isLastPage = currentPage >= this.pageCount;
    if (isLastPage) return;

    const pageListIndex = Math.ceil(currentPage / this.pageViewCount);
    this.currentPageList = pageListIndex + 1;
    currentPage = Math.min(
      (this.currentPageList - 1) * this.pageViewCount + 1,
      this.pageCount,
    );

    reloadGrid(this.gridId, { page: currentPage });
    this.stylePageBtn();
  }

  // 그리드 마지막페이지 이동
  lastPage() {
    reloadGrid(this.gridId, { page: this.pageCount });
    this.stylePageBtn();
  }

  // 그리드 페이지 이동
  goPage(num) {
    $(`#${this.gridId}`)
      .jqGrid('setGridParam', { page: num })
      .trigger('reloadGrid');
    this.stylePageBtn();
  }

  // 그리드 페이지 이동 후, 스타일 정리
  stylePageBtn() {
    const gridWrap = $('#' + this.gridId);
    const currentPage = window.parseInt(gridWrap.getGridParam('page'));
    const gridAreaWrap = gridWrap.parents('.gridArea')[0];
    const pageBtns = gridAreaWrap.querySelectorAll('.grid-page-btn');

    pageBtns.forEach(pageBtn => {
      const pageIndex = window.parseInt(pageBtn.getAttribute('pageIndex'));
      pageBtn.classList.remove('on');
      pageBtn.innerHTML = pageIndex;

      if (pageIndex === currentPage) {
        pageBtn.classList.add('on');
        pageBtn.innerHTML = `<em>${pageIndex}</em>`;
      }
    });
  }
}

// grid.js 파일에서만 사용하는 함수
function saveGridOptions(gridId, gridOptions) {
  window.gridOptions = window.gridOptions || {};
  window.gridOptions[gridId] = gridOptions;
}

function loadGridOptions(gridId) {
  const gridOptions = window.gridOptions?.[gridId];
  return gridOptions;
}
